import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  fetchSalonReviewSuggestions,
  fetchSalonReviews,
  replyToReview,
  setReviewVisibility,
} from '../../api/reviews';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { useToast } from '../../context/ToastContext';

const SalonReviewsPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchParams] = useSearchParams();
  const [typeFilter, setTypeFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState(
    () => searchParams.get('visibility') || ''
  );

  useEffect(() => {
    const v = searchParams.get('visibility');
    if (v === 'pending' || v === 'published') setVisibilityFilter(v);
  }, [searchParams]);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyingId, setReplyingId] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['salon-review-suggestions', typeFilter, visibilityFilter],
    fetchSuggestions: (q) =>
      fetchSalonReviewSuggestions({
        q,
        type: typeFilter || undefined,
        visibility: visibilityFilter || undefined,
      }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['salon-manage-reviews', page, pageSize, typeFilter, visibilityFilter, appliedSearch],
    queryFn: () =>
      fetchSalonReviews({
        page,
        limit: pageSize,
        search: appliedSearch || undefined,
        ...(typeFilter ? { type: typeFilter } : {}),
        ...(visibilityFilter ? { visibility: visibilityFilter } : {}),
      }),
  });

  const reviews = data?.data || [];
  const pagination = data?.pagination;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['salon-manage-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['salon-reviews'] });
    queryClient.invalidateQueries({ queryKey: ['discover-salon'] });
  };

  const replyMut = useMutation({
    mutationFn: ({ id, message }) => replyToReview(id, message),
    onSuccess: () => {
      showToast(t('toast.reviewReplySaved'), 'success');
      setReplyingId(null);
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const visibilityMut = useMutation({
    mutationFn: ({ id, isVisible }) => setReviewVisibility(id, isVisible),
    onSuccess: (_, { isVisible }) => {
      showToast(
        isVisible ? t('toast.reviewPublished') : t('toast.reviewUnpublished'),
        'success'
      );
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const typeFilters = ['', 'SALON', 'SERVICE', 'PRODUCT'];
  const visibilityFilters = ['', 'pending', 'published'];

  return (
    <DashboardShell title={t('reviews.salonTitle')} subtitle={t('reviews.salonSubtitle')}>
      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={t('reviews.salonSearchPlaceholder')}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
        showFiltersToggle
      >
      <div className="flex flex-wrap gap-2">
        {typeFilters.map((f) => (
          <button
            key={f || 'all-type'}
            type="button"
            onClick={() => { setTypeFilter(f); setPage(1); }}
            className={`rounded-full px-3 py-1 text-xs ${typeFilter === f ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
          >
            {f ? t(`reviews.types.${f}`) : t('common.all')}
          </button>
        ))}
      </div>
      <div className="mb-6 flex flex-wrap gap-2">
        {visibilityFilters.map((f) => (
          <button
            key={f || 'all-vis'}
            type="button"
            onClick={() => { setVisibilityFilter(f); setPage(1); }}
            className={`rounded-full px-3 py-1 text-xs ${visibilityFilter === f ? 'bg-amber-600/30 text-amber-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
          >
            {f === 'pending'
              ? t('reviews.filterPending')
              : f === 'published'
                ? t('reviews.filterPublished')
                : t('reviews.filterAllVisibility')}
          </button>
        ))}
      </div>
      </ListSearchToolbar>

      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r._id} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[10px] uppercase">
                    {t(`reviews.types.${r.type}`)}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] ${
                      r.isVisible
                        ? 'bg-emerald-900/40 text-emerald-300'
                        : 'bg-amber-900/40 text-amber-300'
                    }`}
                  >
                    {r.isVisible ? t('reviews.badgePublished') : t('reviews.badgePending')}
                  </span>
                </div>
                <p className="mt-1 font-medium">{r.clientFirstName}</p>
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{r.subjectName}</p>
                <p className="text-xs text-amber-400">{'★'.repeat(r.rating)}</p>
                <p className="text-xs text-zinc-500">{new Date(r.createdAt).toLocaleString()}</p>
              </div>
              <div className="flex flex-col gap-2">
                {!r.isVisible ? (
                  <button
                    type="button"
                    disabled={visibilityMut.isPending}
                    onClick={() => visibilityMut.mutate({ id: r._id, isVisible: true })}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                  >
                    {t('reviews.publishToOthers')}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={visibilityMut.isPending}
                    onClick={() => visibilityMut.mutate({ id: r._id, isVisible: false })}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs"
                  >
                    {t('reviews.hideFromOthers')}
                  </button>
                )}
              </div>
            </div>
            {r.comment && <p className="mt-3 text-sm">{r.comment}</p>}

            {r.reply?.message ? (
              <div className="mt-4 rounded-lg border border-glow-800/30 bg-glow-950/20 p-3">
                <p className="text-xs font-medium text-glow-300">{t('reviews.yourReply')}</p>
                <p className="mt-1 text-sm">{r.reply.message}</p>
                <button
                  type="button"
                  className="mt-2 text-xs text-glow-400 hover:underline"
                  onClick={() => {
                    setReplyingId(r._id);
                    setReplyDrafts((d) => ({ ...d, [r._id]: r.reply.message }));
                  }}
                >
                  {t('reviews.editReply')}
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="mt-3 text-xs text-glow-400 hover:underline"
                onClick={() => setReplyingId(r._id)}
              >
                {t('reviews.addReply')}
              </button>
            )}

            {replyingId === r._id && (
              <div className="mt-3 space-y-2">
                <textarea
                  value={replyDrafts[r._id] ?? r.reply?.message ?? ''}
                  onChange={(e) =>
                    setReplyDrafts((d) => ({ ...d, [r._id]: e.target.value }))
                  }
                  rows={3}
                  placeholder={t('reviews.replyPlaceholder')}
                  className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={replyMut.isPending || !replyDrafts[r._id]?.trim()}
                    onClick={() =>
                      replyMut.mutate({ id: r._id, message: replyDrafts[r._id].trim() })
                    }
                    className="rounded-lg bg-glow-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                  >
                    {t('reviews.postReply')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReplyingId(null)}
                    className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs"
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {!isLoading && reviews.length === 0 && (
        <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('reviews.noReviews')}</p>
      )}

      <PaginatedFooter
        pagination={pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />
    </DashboardShell>
  );
};

export default SalonReviewsPage;
